<script setup>
import {
  LayoutPanelLeft,
  X,
  Home,
  FileText,
  Files,
  Image,
  Palette,
  Puzzle,
  Users,
  Settings,
  LogOut,
  ChevronRight,
  Sun,
  Moon,
  Monitor,
} from 'lucide-vue-next';
import { useColorScheme } from '@/assets/js/functions/useColorScheme.js';
import { lmnFetch } from '@/assets/js/functions/lmnFetch.js';
import { encodeToHash } from '@/assets/js/functions/encodeToHash.js';
import { ref, computed, onMounted } from 'vue';

import AppSearch from '@/components/app/search/index.vue';
import WpToolBar from '@/components/app/wp-toolbar/index.vue';
import { useAppStore } from '@/store/app/app.js';

defineProps({
  showClose: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);

const appStore = useAppStore();
const { userPreference, setColorScheme, globalOverride, prefersDark } =
  useColorScheme();

const gravatar = ref(false);
const isLoggingOut = ref(false);

const caps = computed(() => appStore.state.currentUser?.allcaps || {});
const userCanManageOptions = computed(() => !!caps.value.manage_options);
const userCanEditPosts = computed(() => !!caps.value.edit_posts);
const userCanEditPages = computed(() => !!caps.value.edit_pages);
const userCanUploadFiles = computed(() => !!caps.value.upload_files);
const userCanListUsers = computed(() => !!caps.value.list_users);
const userCanInstallPlugins = computed(() => !!caps.value.install_plugins);
const userCanSwitchThemes = computed(() => !!caps.value.switch_themes);

const quickLinks = computed(() => {
  const base = appStore.state.adminUrl || '/wp-admin/';
  const links = [
    { label: __('Dashboard', 'uixpress'), icon: Home, href: base },
  ];
  if (userCanEditPosts.value)
    links.push({
      label: __('Posts', 'uixpress'),
      icon: FileText,
      href: `${base}edit.php`,
    });
  if (userCanEditPages.value)
    links.push({
      label: __('Pages', 'uixpress'),
      icon: Files,
      href: `${base}edit.php?post_type=page`,
    });
  if (userCanUploadFiles.value)
    links.push({
      label: __('Media', 'uixpress'),
      icon: Image,
      href: `${base}upload.php`,
    });
  if (userCanSwitchThemes.value)
    links.push({
      label: __('Appearance', 'uixpress'),
      icon: Palette,
      href: `${base}themes.php`,
    });
  if (userCanInstallPlugins.value)
    links.push({
      label: __('Plugins', 'uixpress'),
      icon: Puzzle,
      href: `${base}plugins.php`,
    });
  if (userCanListUsers.value)
    links.push({
      label: __('Users', 'uixpress'),
      icon: Users,
      href: `${base}users.php`,
    });
  if (userCanManageOptions.value)
    links.push({
      label: __('Settings', 'uixpress'),
      icon: Settings,
      href: `${base}options-general.php`,
    });
  return links;
});

const themeOptions = [
  { value: 'system', icon: Monitor, label: __('System', 'uixpress') },
  { value: 'light', icon: Sun, label: __('Light', 'uixpress') },
  { value: 'dark', icon: Moon, label: __('Dark', 'uixpress') },
];

const userInitial = computed(
  () => appStore.state.userName?.charAt(0)?.toUpperCase() || '?'
);

const setGravatar = async () => {
  const hashHex = await encodeToHash(appStore.state.userEmail);
  gravatar.value = `https://gravatar.com/avatar/${hashHex}?d=blank`;
};

const logoutUser = async () => {
  isLoggingOut.value = true;
  await lmnFetch({ endpoint: 'uixpress/v1/logout', params: {}, type: 'POST' });
  isLoggingOut.value = false;
  window.location = appStore.state.loginUrl;
};

onMounted(setGravatar);
</script>

<template>
  <div class="panel-content flex h-full flex-col bg-zinc-950 text-zinc-300">
    <!-- Header -->
    <header
      class="flex shrink-0 items-center justify-between gap-3 border-b border-zinc-800/80 bg-zinc-900/50 px-4 py-3.5 backdrop-blur-sm"
    >
      <a
        :href="appStore.state.adminUrl"
        class="group flex min-w-0 flex-1 items-center gap-3 text-inherit no-underline outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 rounded-lg -m-1 p-1"
      >
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-zinc-700/50 bg-zinc-800/80 shadow-inner"
        >
          <img
            v-if="appStore.state?.uixpress_settings?.logo"
            :src="
              prefersDark && appStore.state?.uixpress_settings?.dark_logo
                ? appStore.state.uixpress_settings.dark_logo
                : appStore.state.uixpress_settings.logo
            "
            class="h-5 w-5 object-contain"
            alt=""
          />
          <LayoutPanelLeft v-else :size="20" class="text-zinc-400" />
        </div>
        <div class="min-w-0 flex-1">
          <div
            class="truncate text-sm font-semibold tracking-tight text-zinc-100"
          >
            {{ __('Admin panel', 'uixpress') }}
          </div>
          <div class="truncate text-xs text-zinc-500">WordPress</div>
        </div>
      </a>
      <button
        v-if="showClose"
        type="button"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-zinc-500 outline-none transition-colors hover:bg-zinc-800 hover:text-zinc-200 focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
        :title="__('Close panel', 'uixpress')"
        @click="emit('close')"
      >
        <X :size="18" stroke-width="2" />
      </button>
    </header>

    <!-- Search -->
    <div
      v-if="!appStore.state?.uixpress_settings?.disable_search"
      class="shrink-0 border-b border-zinc-800/80 bg-zinc-900/30 px-3 py-3"
    >
      <AppSearch />
    </div>

    <!-- Main content -->
    <div class="flex min-h-0 flex-1 flex-col">
      <div
        class="panel-content__scroll flex-1 overflow-x-hidden overflow-y-auto px-3 py-4"
      >
        <!-- Toolbar section -->
        <section class="panel-section">
          <h3 class="panel-section__title">
            {{ __('Toolbar', 'uixpress') }}
          </h3>
          <div class="panel-toolbar">
            <WpToolBar />
          </div>
        </section>

        <!-- Quick links section -->
        <section class="panel-section mt-4">
          <h3 class="panel-section__title">
            {{ __('Quick links', 'uixpress') }}
          </h3>
          <nav class="flex flex-col gap-1" role="navigation">
            <a
              v-for="link in quickLinks"
              :key="link.href"
              :href="link.href"
              class="group flex items-center gap-3 rounded-xl border border-transparent bg-zinc-900/60 px-3 py-2.5 text-zinc-300 no-underline transition-all duration-200 hover:border-zinc-700/50 hover:bg-zinc-800/60 hover:text-zinc-100 focus-visible:border-indigo-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30"
            >
              <div
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-800/80 text-zinc-500 transition-colors duration-200 group-hover:bg-indigo-500/20 group-hover:text-indigo-400"
              >
                <component :is="link.icon" :size="18" stroke-width="2" />
              </div>
              <span class="min-w-0 flex-1 truncate text-sm font-medium">
                {{ link.label }}
              </span>
              <ChevronRight
                :size="16"
                class="shrink-0 text-zinc-600 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-zinc-400"
                stroke-width="2"
              />
            </a>
          </nav>
        </section>
      </div>
    </div>

    <!-- Footer -->
    <footer
      class="flex shrink-0 flex-col gap-3 border-t border-zinc-800/80 bg-zinc-900/80 px-4 py-3 backdrop-blur-sm"
    >
      <!-- Theme switcher -->
      <div
        v-if="!globalOverride"
        class="flex gap-0.5 rounded-xl bg-zinc-800/60 p-1"
      >
        <button
          v-for="opt in themeOptions"
          :key="opt.value"
          type="button"
          :class="[
            'flex flex-1 items-center justify-center gap-1.5 rounded-lg border-0 px-2.5 py-2 text-xs font-medium capitalize outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900',
            userPreference === opt.value
              ? 'bg-zinc-700 text-zinc-100 shadow-sm'
              : 'bg-transparent text-zinc-500 hover:bg-zinc-700/50 hover:text-zinc-300',
          ]"
          :title="opt.label"
          @click="setColorScheme(opt.value)"
        >
          <component :is="opt.icon" :size="14" stroke-width="2" />
          <span>{{ opt.label }}</span>
        </button>
      </div>

      <!-- User profile -->
      <div
        class="flex items-center gap-3 rounded-xl border border-zinc-800/50 bg-zinc-800/40 px-3 py-2.5"
      >
        <div
          class="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-700 ring-2 ring-zinc-600/50"
        >
          <span class="text-sm font-semibold text-zinc-200">
            {{ userInitial }}
          </span>
          <img
            v-if="gravatar"
            :src="gravatar"
            class="absolute inset-0 h-full w-full object-cover"
            alt=""
          />
        </div>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-semibold text-zinc-100">
            {{ appStore.state.userName }}
          </div>
          <div class="truncate text-xs text-zinc-500">
            {{ appStore.state.userEmail }}
          </div>
        </div>
        <button
          type="button"
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-zinc-500 outline-none transition-colors hover:bg-zinc-700/50 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900"
          :disabled="isLoggingOut"
          :title="__('Logout', 'uixpress')"
          @click="logoutUser"
        >
          <LogOut :size="18" stroke-width="2" />
        </button>
      </div>
    </footer>
  </div>
</template>

<style scoped>
@reference "tailwindcss";
.panel-content {
  --panel-radius: 0.75rem;
}

.panel-section {
  @apply rounded-xl border border-zinc-800/60 bg-zinc-900/50 p-3;
}

.panel-section__title {
  @apply mb-2.5 px-0.5 text-[0.6875rem] font-semibold uppercase tracking-widest text-zinc-500;
}

/* Custom scrollbar for panel content */
.panel-content__scroll {
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgb(82 82 91 / 0.4) transparent;
}

.panel-content__scroll::-webkit-scrollbar {
  width: 6px;
}

.panel-content__scroll::-webkit-scrollbar-track {
  @apply bg-transparent rounded-full;
}

.panel-content__scroll::-webkit-scrollbar-thumb {
  @apply rounded-full bg-zinc-600/40 transition-colors duration-200;
}

.panel-content__scroll::-webkit-scrollbar-thumb:hover {
  @apply bg-zinc-600/60;
}

/* WordPress toolbar layout overrides for panel context */
.panel-toolbar :deep(#uix-wp-toolbar-host) {
  display: flex !important;
  flex-direction: column !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  list-style: none !important;
  gap: 0 !important;
}

.panel-toolbar :deep(#wp-toolbar) {
  display: flex !important;
  flex-direction: column !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  gap: 0 !important;
  min-height: unset !important;
}

.panel-toolbar :deep(#wp-admin-bar-root-default),
.panel-toolbar :deep(#wp-admin-bar-top-secondary) {
  display: flex !important;
  flex-direction: column !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  gap: 0 !important;
  flex-wrap: nowrap !important;
}

.panel-toolbar :deep(.ab-sub-wrapper) {
  display: none !important;
}

.panel-toolbar :deep(li) {
  display: block !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  float: none !important;
}

.panel-toolbar :deep(li > .ab-item),
.panel-toolbar :deep(li > a) {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  width: 100% !important;
  padding: 0.45rem 0.55rem !important;
  margin: 0 !important;
  border-radius: 0.5rem !important;
  font-size: 0.8125rem !important;
  font-weight: 500 !important;
  color: rgb(161 161 170) !important;
  text-decoration: none !important;
  cursor: pointer !important;
  box-sizing: border-box !important;
  transition: background-color 0.15s, color 0.15s !important;
}

.panel-toolbar :deep(li > .ab-item:hover),
.panel-toolbar :deep(li > a:hover) {
  background-color: rgb(39 39 42 / 0.8) !important;
  color: rgb(244 244 245) !important;
}

.panel-toolbar :deep(.ab-icon) {
  height: 1rem !important;
  min-height: 1rem !important;
  min-width: 1rem !important;
  width: 1rem !important;
  flex-shrink: 0 !important;
}

.panel-toolbar :deep(.ab-icon::before) {
  display: block !important;
}
</style>
